import { useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  BadgeCheck,
  CircleSlash,
  Briefcase,
} from "lucide-react";
import { Link } from "react-router";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import InnerLoader from "../../../component/ui/loader/inner-loader";
import { GlobalContext } from "../../../context/Context";
import moment from "moment";
import DataNotFound from "../../../component/ui/loader/data-not-found";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span className={classNames("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", tones[tone])}>
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default", valueColor="" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500">{title}</div>
          <div className={`text-lg font-semibold break-all !text-[16px] tracking-tight capitalize ${valueColor}`}>{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

export default function CarDamage({ open = true, onClose = () => { }, data, onReload = () => {} }) {

  let { state } = useContext(GlobalContext);

  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState({});
  const [activeTab , setActiveTab] = useState("Summary");

  const getData = async () => {
    let apiData = `get-car-damage-record-detail?damage_id=${data?.damage_id}`;

    try {
      let res = await apiCall({ method: 'get', url: apiData });
      setDetails(res?.carDamageRecordDetail);
      setIsLoading(true);
    } catch (error) {
      console.warn("Error : ", error)
    }
  }

  // useOnClickOutside(containerRef, () => onClose());

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    console.log("data", data)
    getData();
  }, [data]);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.aside
              ref={containerRef}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[1000px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
              aria-modal
              role="dialog"
            >
              {
                isLoading ? (
                  <>
                    {/* Header */}
                    <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full relative z-30">
                      <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                        <div className="flex min-w-0 items-center gap-3">
                          {
                            details?.car_detail?.car_image ?

                            <img
                              src={details?.car_detail?.car_image}
                              alt={details?.car_detail?.car_name}
                              className="size-12 rounded-2xl object-cover ring-2 ring-white shadow"
                            />
                            :
                            <div className="size-12 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 grid place-items-center text-sm font-medium">{details?.car_detail?.car_name?.split(" ")?.[0]?.charAt(0)}{details?.car_detail?.car_name?.split(" ")?.[1]?.charAt(0)}</div>
                          }
                          <div className="min-w-0">
                            <div className="flex-col">
                              <div className="flex gap-2 items-center">
                                <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                                  {details?.car_detail?.car_name}
                                </h2>
                                <Badge tone={details?.damage_status == "Open" ? "green" : "red"} icon={details?.damage_status == "Open" ? BadgeCheck : CircleSlash}>{details?.damage_status}</Badge>
                              </div>
                              <div className="flex gap-2 items-center">
                                <Badge tone="blue" icon={Briefcase}>{details?.car_detail?.car_registraion}</Badge>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {/* <div
                            className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            onClick={(details?.car_assigned && details?.car_assigned != "-") ? unAssignUser : assignUser}
                          >
                            Edit Detail
                          </div> */}
                          <button
                             className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                            onClick={onClose}
                            aria-label="Close"
                          >
                            <X className="size-4" /> Exit
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="grid min-h-0 flex-1 grid-cols-12 gap-4 w-full overflow-hidden p-4">
                      {/* Left: main content */}
                      <div className="col-span-12 flex min-h-0 flex-col w-full gap-4 lg:col-span-12">
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                          <Stat title={"Assigned User"} value={details?.employee_name || "-"} />
                          <Stat title={"Incident Date"} value={moment(details?.incident_date).format("DD MMM YYYY") || "-"} />
                          <Stat title={"Incident Type"} value={details?.incident_type || "-"} />
                          <Stat title={"Severity"} value={details?.severity ?? "-"} />
                        </div>

                        <div className="flex items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-1">
                            <button
                              // key={t.key}
                              onClick={() => setActiveTab("Summary")}
                              className={classNames(
                                "rounded-md px-[12px] py-[8px] text-sm font-medium border border-solid border-[#E1E4EA]",
                                activeTab == "Summary"
                                  ? "bg-[#F5F5F5] text-[#0E121B]"
                                  : "text-slate-600 hover:bg-slate-100"
                              )}
                               
                            >
                              {"Summary"}
                            </button>

                            <button
                              // key={t.key}
                              onClick={() => setActiveTab("Incident Images")}
                              className={classNames(
                                "rounded-md px-[12px] py-[8px] text-sm font-medium border border-solid border-[#E1E4EA]",
                                activeTab == "Incident Images"
                                  ? "bg-[#F5F5F5] text-[#0E121B]"
                                  : "text-slate-600 hover:bg-slate-100"
                              )}
                            >
                              {"Incident Images"}
                            </button>
                          </div>
                        </div>

                        <div className="min-h-0 overflow-auto rounded-xl w-full pb-2 relative">
                          { activeTab == "Summary" ?
                            <div className="grid grid-cols-1 gap-3">
                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Incident Location</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{details?.incident_location ?? "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Damage Description</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{details?.damage_description ?? "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Police Report Number</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{details?.police_report_number ?? "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Repair Cost</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{details?.repair_cost ?? "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Insurance Claim Status</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{details?.insurance_claim_status ?? "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Repair Cost Breakup (Employee)</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{JSON.parse(details?.repair_cost_breakup)?.employee ? `${JSON.parse(details?.repair_cost_breakup)?.employee}%` : "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Repair Cost Breakup (Employer)</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{JSON.parse(details?.repair_cost_breakup)?.employer ? `${JSON.parse(details?.repair_cost_breakup)?.employer}%` : "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Repair Cost Breakup (Vendor)</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{JSON.parse(details?.repair_cost_breakup)?.vendor ? `${JSON.parse(details?.repair_cost_breakup)?.vendor}%` : "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Repair Cost Breakup (Insurance)</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{JSON.parse(details?.repair_cost_breakup)?.insurance ? `${JSON.parse(details?.repair_cost_breakup)?.insurance}%` : "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Final Comment</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{details?.final_comment ?? "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Invoice</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{details?.invoice ? <Link to={details?.invoice} target="_blank" className="underline decoration-solid hover:text-sky-800">View</Link> : "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Damage Status</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{details?.damage_status ?? "-"}</span>
                                </div>
                              </div>

                              <div className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>
                                <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                  <span>Description</span>
                                </div>
                                <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                  <span>{details?.description ?? "-"}</span>
                                </div>
                              </div>
                            </div>
                            :
                            null
                          }
                          {
                            activeTab == "Incident Images" ?
                            JSON.parse(details?.incident_images)?.length ?
                            <div className="grid grid-cols-1 gap-3">
                              {JSON.parse(details?.incident_images)?.map((eachImg, i) => (
                                <div key={i} className={`grid grid-cols-8 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50`}>

                                  <div className="col-span-4 flex gap-2 text-[13px] font-medium text-slate-500">
                                    <span>Image {i + 1}</span>
                                  </div>

                                  <div className="col-span-4 flex flex-col gap-2 text-[13px] font-medium text-slate-500">
                                    <span><Link to={eachImg} target="_blank" className="underline decoration-solid hover:text-sky-800">View</Link></span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            :
                            <DataNotFound title={"Incident Images"} />
                            :
                            null
                          }
                          {/* {
                            !details?.mileage_details?.length && !details?.maintenance_details?.length ?
                            <DataNotFound title={"Data"} />
                            :
                            null
                          } */}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <InnerLoader wrapperClass={'w-full'} />
                )
              }
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}